import React from "react";
import { motion, Variants } from "framer-motion";
import { TypeAnimation } from "react-type-animation";
import { useTranslation } from "react-i18next";
import Section from "../components/Section";
import cls from "./About.module.css";
import arrow from "../assets/arrow_outward.png";
import satbayev from "../assets/about-su-bg.png";

interface AboutProps {
  isActive: boolean;
}

// === НАСТРОЙКИ АНИМАЦИИ ===
const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2,
    },
  },
};

const fadeUpVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 30,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

const slideRightVariants: Variants = {
  hidden: {
    opacity: 0,
    x: -40,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.8,
      ease: "easeOut",
    },
  },
};

const buttonVariants: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.8,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 120,
      damping: 12,
    },
  },
};

const About: React.FC<AboutProps> = ({ isActive }) => {
  const { t, i18n } = useTranslation();

  const bgText =
    "Кеңестік геолог-ғалым, ғылымды ұйымдастырушы және қоғам қайраткері, геология-минералогия ғылымдарының докторы (1942), профессор (1950), Қазақ КСР Ғылым академиясының академигі (1946), КСРО Ғылым академиясының академигі (1946). Қазақ КСР Ғылым академиясының тұңғыш Президенті.";

  // Ссылка на курс зависит от выбранного языка
  const studyLink =
    i18n.language === "ru"
      ? "https://polytechonline.kz/cabinet/course/view.php?id=14223"
      : "https://polytechonline.kz/cabinet/course/view.php?id=14224";

  return (
    <Section isActive={isActive}>
      <div className={cls.aboutSection}>
        <motion.div
          className={cls.container}
          variants={containerVariants}
          initial="hidden"
          animate={isActive ? "visible" : "hidden"}
        >
          <motion.h2
            className={cls.title}
            variants={fadeUpVariants}
            dangerouslySetInnerHTML={{
              __html: t("about.title"),
            }}
          />

          <div className={cls.cardBlock}>
            <motion.div
              className={cls.imgBlock}
              variants={slideRightVariants}
            >
              {isActive && (
                <div className={cls.typeWrapper}>
                  <TypeAnimation
                    key={
                      isActive
                        ? `active-${i18n.language}`
                        : "inactive"
                    }
                    sequence={[bgText]}
                    speed={70}
                    cursor={false}
                    repeat={0}
                    className={cls.animatedBgText}
                  />
                </div>
              )}

              <img
                src={satbayev}
                alt=""
                className={cls.mainPhoto}
              />
            </motion.div>

            <div className={cls.cardBlockRight}>
              <div className={cls.description}>
                <motion.p variants={fadeUpVariants}>
                  {t("about.desc1")}
                </motion.p>

                <motion.p variants={fadeUpVariants}>
                  {t("about.desc2")}
                </motion.p>

                <motion.p variants={fadeUpVariants}>
                  {t("about.desc3")}
                </motion.p>
              </div>

              <div className={cls.btnsBlock}>
                {/* ОБУЧЕНИЕ */}
                <motion.a
                  className={cls.btnItem}
                  href={studyLink}
                  variants={buttonVariants}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {t("about.btns.study")}

                  <img
                    src={arrow}
                    alt=""
                    style={{ marginLeft: 10 }}
                  />
                </motion.a>

                {/* SATBAYEV AI */}
                <motion.a
                  className={cls.btnItem}
                  href="https://satbayev.ai/"
                  target="_blank"
                  rel="noopener noreferrer"
                  variants={buttonVariants}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {t("about.btns.ai")}

                  <img
                    src={arrow}
                    alt=""
                    style={{ marginLeft: 10 }}
                  />
                </motion.a>

                {/* БИБЛИОТЕКА */}
                <motion.a
                  className={cls.btnItem}
                  href="https://e-lib.satbayev.university/MegaPro/Web/Search/Ext"
                  variants={buttonVariants}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {t("about.btns.library")}

                  <img
                    src={arrow}
                    alt=""
                    style={{ marginLeft: 10 }}
                  />
                </motion.a>

                {/* ИСТОРИЯ */}
                <motion.a
                  className={cls.btnItem}
                  href="https://satbayev.university/kk/history"
                  variants={buttonVariants}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {t("about.btns.history")}

                  <img
                    src={arrow}
                    alt=""
                    style={{ marginLeft: 10 }}
                  />
                </motion.a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </Section>
  );
};

export default About;